import moment from 'moment-timezone';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_KEY_FORMAT = 'HH:mm';
// Time pickers show titles in 'LT' format: '2:00 PM' for en, '14:00' for most other locales.
const TIME_TITLE_FORMATS = ['h:mm A', 'H:mm'];
// Wall time used when a date is picked but no time yet. DST and offset changes happen in
// the early hours, so midday gives the abbreviation that applies to most of that day.
const DEFAULT_LABEL_TIME_KEY = '12:00';

/**
 * Returns the time zone abbreviation (e.g. 'PDT', 'MST') of `timezone` at the picked
 * wall-clock date and time, so a label next to a picker describes the date being picked
 * rather than today. America/Vancouver gives 'PDT' for 2026-10-15 and 'MST' for 2026-11-12.
 *
 * Falls back to today in `timezone` when no date is picked, and to now when nothing is.
 *
 * @param {string} timezone IANA time zone name of the listing, e.g. 'America/Vancouver'
 * @param {moment.Moment|string|null} [date] picked date, as a moment or a 'YYYY-MM-DD' string
 * @param {string|null} [timeKey] picked time in 'HH:mm', e.g. '14:30'
 * @returns {string} the abbreviation, or '' when the listing has no time zone
 */
export const getTimezoneAbbreviation = (timezone, date, timeKey) => {
  if (!timezone) return '';

  const pickedDate = date ? moment(date, DATE_FORMAT) : null;
  const hasPickedDate = Boolean(pickedDate && pickedDate.isValid());
  if (!hasPickedDate && !timeKey) return moment.tz(timezone).format('z');

  const dateString = hasPickedDate ? pickedDate.format(DATE_FORMAT) : moment.tz(timezone).format(DATE_FORMAT);
  return moment
    .tz(`${dateString} ${timeKey || DEFAULT_LABEL_TIME_KEY}`, `${DATE_FORMAT} ${TIME_KEY_FORMAT}`, timezone)
    .format('z');
};

/**
 * Converts a time picker title ('2:00 PM' or '14:00') to its 'HH:mm' key.
 *
 * @param {string|null} timeTitle
 * @returns {string|null} the 'HH:mm' key, or null when nothing is picked or it can't be parsed
 */
export const timeKeyFromTitle = (timeTitle) => {
  if (!timeTitle) return null;
  const time = moment(timeTitle, TIME_TITLE_FORMATS);
  return time.isValid() ? time.format(TIME_KEY_FORMAT) : null;
};
